'use client'

import {useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode} from 'react'
import Link from 'next/link'
import {AlertCircle, CalendarCheck, Check, ChevronDown, ChevronLeft, ChevronRight, CircleHelp, Clock3, Hourglass, Loader2, MapPin, Ticket, UserRound} from 'lucide-react'
import {DetailsFact, DetailsFactIcon, DetailsFacts, DetailsPeopleIcon} from '../shared/DetailsFact'
import {useTranslation} from '../../../hooks/useTranslation'
import {GuestSignInHint} from './GuestSignInHint'
import {GuestContactFields} from './GuestContactFields'
import {cn, formatInstructorNames} from '../../../lib/utils'
import {
    commitGuestCheckout,
    GuestCheckoutError,
    isGuestCheckoutCommitRefusal,
    previewIndividualTrainingGuestCheckout,
    startGuestCheckoutPayment,
    type GuestCheckoutCommit,
    type GuestCheckoutPreview,
} from '../../../services/guest-checkout.service'
import {
    getPublicPrivateBookingSlots,
    type PublicPrivateBookingOffer,
    type PublicPrivateBookingSlot,
} from '../../../services/public-private-bookings.service'
import type {PublicPartnerProfile} from '../../../services/public-profile.service'
import {PublicScheduleDayPicker} from './PublicScheduleDayPicker'
import {addCalendarDays, isoWeekdayForLocalDate, type DateTimeFormatter} from '@fitro/date-time'
import {PrivateBookingOfferSelector} from '../shared/PrivateBookingOfferSelector'
import {Avatar} from '../../ui/Avatar'
import {Dropdown} from '../../ui/Dropdown/Dropdown'
import {readGuestContactDraft, saveGuestContactDraft} from '../../../lib/guest-contact-draft'
import {focusFirstInvalidGuestField, validateGuestContact, type GuestContactErrors, type GuestContactField} from '../../../lib/guest-contact-validation'
import {usePaymentRedirect} from '../shared/usePaymentRedirect'
import {getButtonClasses} from '@fitro/customer-ui/Button'
import {FADE_IN_CLASS} from '@fitro/customer-ui/Reveal'
import {smoothScrollBehavior} from '@fitro/customer-ui/smoothScrollBehavior'
import {buildAuthHref} from './publicEntryLinks'
import {resolvePublicAssetUrl} from './publicAssetUrl'
import {trackGuestCheckoutRejected, trackGuestCheckoutStarted} from '../../../lib/guest-checkout-journey'
import {reportStudioPrice} from '../../../lib/studio-price-sync'

export type PublicPrivateBookingIntent = {
    locationId?: string | null
    offerId: string
    startAt: string
    trainerId?: string | null
}

export function buildAuthenticatedPrivateBookingUrl(intent: PublicPrivateBookingIntent): string {
    const params = new URLSearchParams({
        offerId: intent.offerId,
        source: 'public-preview',
        startAt: intent.startAt,
    })
    if (intent.trainerId) params.set('trainerId', intent.trainerId)
    if (intent.locationId) params.set('locationId', intent.locationId)
    return `/calendar/individual?${params.toString()}`
}

export function PublicPrivateBookingFlow({
    offers,
    onAuthenticatedBookingIntent,
    partnerSlug,
    profile,
}: {
    offers: PublicPrivateBookingOffer[]
    onAuthenticatedBookingIntent?: (intent: PublicPrivateBookingIntent) => void
    partnerSlug: string
    profile: PublicPartnerProfile | null
}) {
    const {locale, t, dateTime} = useTranslation('landing')
    const {t: tCommon} = useTranslation('common')
    const fieldErrorIdPrefix = useId()
    const checkoutSectionRef = useRef<HTMLElement | null>(null)
    const [selectedOfferId, setSelectedOfferId] = useState('')
    const selectedOffer = offers.find((offer) => offer.id === selectedOfferId) ?? offers[0]
    const hasSelectedOffer = Boolean(selectedOfferId && selectedOffer?.id === selectedOfferId)
    const selectedOfferLocation =
        selectedOffer.allowedLocationIds.length === 0
            ? t('publicEntry.privateBookings.noLocation')
            : selectedOffer.locations?.[0]?.name
                ?? formatBookingLocation(profile, t('publicEntry.privateBookings.locationFallback'))
    const usesAutoAssignedTrainer = hasSelectedOffer && isAutoAssignedTrainerOffer(selectedOffer)
    const instructorOptions = useMemo(
        () => hasSelectedOffer ? buildInstructorOptions(selectedOffer, t('publicEntry.privateBookings.anyInstructor')) : [],
        [hasSelectedOffer, selectedOffer, t]
    )
    const [selectedInstructorId, setSelectedInstructorId] = useState(ANY_INSTRUCTOR_ID)
    const [weekOffset, setWeekOffset] = useState(0)
    const todayKey = dateTime.today()
    const weekRange = useMemo(() => buildBookingWeekRange(todayKey, weekOffset), [todayKey, weekOffset])
    const bookingDays = useMemo(() => buildBookingDays(dateTime, weekRange), [dateTime, weekRange])
    const weekRangeLabel = useMemo(() => formatBookingWeekRange(dateTime, weekRange), [dateTime, weekRange])
    const [selectedDayKey, setSelectedDayKey] = useState<string>(() => todayKey)
    const selectedDayKeyRef = useRef(selectedDayKey)
    const [slots, setSlots] = useState<PublicPrivateBookingSlot[]>([])
    const [slotInstructorCandidates, setSlotInstructorCandidates] = useState<Record<string, string[]>>({})
    const [slotsLoading, setSlotsLoading] = useState(false)
    const [slotsError, setSlotsError] = useState(false)
    const [selectedSlotStartAt, setSelectedSlotStartAt] = useState('')
    const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>('selection')
    // A contact typed in the last 10 minutes comes back; the terms must be accepted again.
    const [customerForm, setCustomerForm] = useState<CustomerForm>(() => {
        const draft = readGuestContactDraft()
        return {
            email: draft?.email ?? '',
            firstName: draft?.firstName ?? '',
            fitroTermsAccepted: false,
            lastName: draft?.lastName ?? '',
            marketingConsent: false,
            phone: draft?.phone ?? '',
            termsAccepted: false,
        }
    })
    const [preview, setPreview] = useState<GuestCheckoutPreview | null>(null)
    const [isPreviewing, setIsPreviewing] = useState(false)
    const [isCommitting, setIsCommitting] = useState(false)
    const [checkoutError, setCheckoutError] = useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = useState<GuestContactErrors>({})
    const redirectToPayment = usePaymentRedirect(() => {
        commitInFlightRef.current = false
        setIsCommitting(false)
    })
    const [previewRequiresSignIn, setPreviewRequiresSignIn] = useState(false)
    // One Idempotency-Key per submitted request: a retry of the same data must replay a Hold the
    // server may already have saved (e.g. 503 after the commit), edited data is a new attempt.
    const commitAttemptRef = useRef<{idempotencyKey: string; request: string} | null>(null)
    const commitInFlightRef = useRef(false)
    // The saved Hold: once the commit succeeded, only the payment start is retried and Back is locked.
    const [heldCheckout, setHeldCheckout] = useState<GuestCheckoutCommit | null>(null)
    // Bumped on every selection change so a preview for the previous slot is discarded.
    const selectionRevisionRef = useRef(0)
    const selectedInstructorQuery = usesAutoAssignedTrainer || selectedInstructorId === ANY_INSTRUCTOR_ID ? null : selectedInstructorId
    const slotsForSelectedDay = useMemo(
        () => slots.filter((slot) => dateTime.dateKeyInZone(slot.startAt) === selectedDayKey),
        [dateTime, selectedDayKey, slots],
    )
    const selectedSlot = selectedSlotStartAt
        ? slots.find((slot) => slot.startAt === selectedSlotStartAt) ?? null
        : null
    const selectedHour = selectedSlot ? dateTime.formatInstantTime(selectedSlot.startAt) : ''
    const selectedInstructor = instructorOptions.find((instructor) => instructor.id === selectedInstructorId) ?? instructorOptions[0]
    const suggestedInstructor = useMemo(() => {
        if (usesAutoAssignedTrainer) {
            return undefined
        }
        if (!selectedSlot || selectedInstructorId !== ANY_INSTRUCTOR_ID) {
            return selectedInstructor
        }
        const candidateIds = slotInstructorCandidates[selectedSlot.startAt] ?? []
        const suggestedInstructorId = pickSuggestedInstructorId(candidateIds, selectedSlot.startAt)
        const suggestedOption = instructorOptions.find((instructor) => instructor.id === suggestedInstructorId)
        if (suggestedOption) return suggestedOption

        const fallbackInstructorId = selectedOffer?.instructors[0]?.id
        return fallbackInstructorId
            ? instructorOptions.find((instructor) => instructor.id === fallbackInstructorId)
            : undefined
    }, [instructorOptions, selectedInstructor, selectedInstructorId, selectedOffer?.instructors, selectedSlot, slotInstructorCandidates, usesAutoAssignedTrainer])
    const bookingInstructor = usesAutoAssignedTrainer ? undefined : suggestedInstructor ?? selectedInstructor
    // The booked trainer's photo in the facts; any trainer, or one the studio assigns, shows the person icon.
    const instructorFactPeople = (name: string) => bookingInstructor && bookingInstructor.id !== ANY_INSTRUCTOR_ID
        ? [{key: bookingInstructor.id, name, avatarUrl: bookingInstructor.avatarUrl}]
        : []
    const daysWithSlots = useMemo(
        () => new Set<string>(slots.flatMap((slot) => {
            const key = dateTime.dateKeyInZone(slot.startAt)
            return key ? [key] : []
        })),
        [dateTime, slots],
    )
    const visibleBookingDays = bookingDays
    const selectedDay = bookingDays.find((day) => day.key === selectedDayKey) ?? bookingDays[0]
    const bookingTrainerId = usesAutoAssignedTrainer || bookingInstructor?.id === ANY_INSTRUCTOR_ID ? null : bookingInstructor?.id ?? null
    // Once previewed, the summary shows what the guest is charged rather than the list price.
    const chargedPrice = formatPrivateBookingPrice(
        preview ? {priceMinor: preview.summary.amountMinor, currency: preview.summary.currency} : selectedOffer,
        locale,
    )
    // Offers the Studio cannot take guest payments for are booked after signing in, with the chosen slot kept.
    const requiresSignIn = hasSelectedOffer && !onAuthenticatedBookingIntent
        && (selectedOffer.guestCheckoutAvailable !== true || previewRequiresSignIn)
    const signInHref = buildAuthHref('/sign-in', selectedSlot
        ? buildAuthenticatedPrivateBookingUrl({
            locationId: selectedSlot.locationId ?? null,
            offerId: selectedOffer.id,
            startAt: selectedSlot.startAt,
            trainerId: bookingTrainerId,
        })
        : '/calendar/individual')

    useEffect(() => {
        selectedDayKeyRef.current = selectedDayKey
    }, [selectedDayKey])

    useEffect(() => {
        let cancelled = false
        if (!partnerSlug || !hasSelectedOffer || !selectedOffer?.id) {
            setSlots([])
            setSlotInstructorCandidates({})
            setSelectedSlotStartAt('')
            return
        }

        setSlotsLoading(true)
        setSlotsError(false)
        const baseQuery = {
            endDate: weekRange.endKey,
            startDate: weekRange.startKey,
        }
        const aggregateRequest = getPublicPrivateBookingSlots(partnerSlug, selectedOffer.id, {
            ...baseQuery,
            trainerId: selectedInstructorQuery,
        })
        const instructorRequests: Promise<SlotInstructorCandidateResponse>[] = usesAutoAssignedTrainer || selectedInstructorQuery
            ? []
            : selectedOffer.instructors.map((instructor) => (
                getPublicPrivateBookingSlots(partnerSlug, selectedOffer.id, {
                    ...baseQuery,
                    trainerId: instructor.id,
                }).then((response) => ({instructorId: instructor.id, slots: response?.slots ?? []}))
            ))

        Promise.all([aggregateRequest, Promise.all(instructorRequests)]).then(([response, candidateResponses]) => {
            if (cancelled) return
            const nextSlots = dedupeSlotsByStartAt(response?.slots ?? [])
            setSlots(nextSlots)
            setSlotInstructorCandidates(
                usesAutoAssignedTrainer
                    ? {}
                    : selectedInstructorQuery
                    ? buildSingleInstructorCandidateMap(nextSlots, selectedInstructorQuery)
                    : buildSlotInstructorCandidateMap(candidateResponses)
            )
            const currentSelectedDayKey = selectedDayKeyRef.current
            const firstSlotForCurrentDay = nextSlots.find((slot) => dateTime.dateKeyInZone(slot.startAt) === currentSelectedDayKey)
            setSelectedSlotStartAt(firstSlotForCurrentDay?.startAt ?? '')
            setSlotsError(!response)
        }).catch(() => {
            if (cancelled) return
            setSlots([])
            setSlotInstructorCandidates({})
            setSelectedSlotStartAt('')
            setSlotsError(true)
        }).finally(() => {
            if (!cancelled) setSlotsLoading(false)
        })

        return () => {
            cancelled = true
        }
    }, [dateTime, hasSelectedOffer, partnerSlug, selectedOffer?.id, selectedOffer.instructors, selectedInstructorQuery, usesAutoAssignedTrainer, weekRange.endKey, weekRange.startKey])

    function selectWeek(nextWeekOffset: number) {
        const nextDays = buildBookingDays(dateTime, buildBookingWeekRange(todayKey, nextWeekOffset))
        const nextSelectedDay = resolveDefaultBookingDay(todayKey, nextDays, nextWeekOffset)
        setWeekOffset(nextWeekOffset)
        setSelectedDayKey(nextSelectedDay?.key ?? '')
        setSelectedSlotStartAt('')
        resetCheckoutProgress()
    }

    function selectDay(dayKey: string) {
        const nextSlot = slots.find((slot) => dateTime.dateKeyInZone(slot.startAt) === dayKey)
        setSelectedDayKey(dayKey)
        setSelectedSlotStartAt(nextSlot?.startAt ?? '')
        resetCheckoutProgress()
    }

    function resetCheckoutProgress() {
        selectionRevisionRef.current += 1
        setCheckoutStep('selection')
        setPreview(null)
        setPreviewRequiresSignIn(false)
        setCheckoutError(null)
        setFieldErrors({})
        commitAttemptRef.current = null
        setHeldCheckout(null)
    }

    async function goToCheckoutDetails() {
        setCheckoutError(null)
        if (!hasSelectedOffer || !selectedSlot?.startAt || isPreviewing) return
        const trainerId = usesAutoAssignedTrainer ? null : bookingInstructor?.id ?? null
        if (onAuthenticatedBookingIntent) {
            onAuthenticatedBookingIntent({
                locationId: selectedSlot.locationId ?? null,
                offerId: selectedOffer.id,
                startAt: selectedSlot.startAt,
                trainerId,
            })
            return
        }
        if (!usesAutoAssignedTrainer && (!trainerId || trainerId === ANY_INSTRUCTOR_ID)) {
            setCheckoutError(t('publicEntry.privateBookings.checkout.instructorRequiredError'))
            return
        }
        setIsPreviewing(true)
        const revision = selectionRevisionRef.current
        try {
            const nextPreview = await previewIndividualTrainingGuestCheckout(partnerSlug, selectedOffer.id, {
                locationId: selectedSlot.locationId ?? null,
                startAt: selectedSlot.startAt,
                trainerId,
            })
            if (selectionRevisionRef.current !== revision) return
            setPreview(nextPreview)
            trackGuestCheckoutStarted()
            reportStudioPrice({kind: 'privateBooking', id: selectedOffer.id, amountMinor: nextPreview.summary.amountMinor}).catch(() => undefined)
        } catch (error) {
            trackGuestCheckoutRejected(error)
            if (selectionRevisionRef.current === revision) {
                setCheckoutError(guestCheckoutErrorMessage(error, t))
                setPreviewRequiresSignIn(error instanceof GuestCheckoutError && error.code === 'login_required')
            }
            return
        } finally {
            setIsPreviewing(false)
        }
        setCheckoutStep('details')
        if (typeof window === 'undefined' || !window.matchMedia('(max-width: 1023px)').matches) {
            return
        }

        window.requestAnimationFrame(() => {
            checkoutSectionRef.current?.scrollIntoView({behavior: smoothScrollBehavior(), block: 'start'})
        })
    }

    function updateCustomerForm(field: keyof CustomerForm, value: string | boolean) {
        setCustomerForm((current) => ({...current, [field]: field === 'phone' && typeof value === 'string' ? normalizePhoneInput(value) : value}))
        setCheckoutError(null)
        if (field === 'marketingConsent') return
        const contactField: GuestContactField = field === 'termsAccepted' ? 'studioTermsAccepted' : field
        setFieldErrors((current) => {
            const next = {...current}
            delete next[contactField]
            return next
        })
    }



    function handleDetailsSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()
        setCheckoutError(null)

        const firstName = customerForm.firstName.trim()
        const lastName = customerForm.lastName.trim()
        const email = customerForm.email.trim()
        const phone = customerForm.phone.trim()
        const errors = validateGuestContact({
            firstName,
            lastName,
            email,
            phone,
            studioTermsAccepted: customerForm.termsAccepted,
            fitroTermsAccepted: customerForm.fitroTermsAccepted,
        })
        setFieldErrors(errors)
        if (focusFirstInvalidGuestField(event.currentTarget, errors)) return

        if (!preview) {
            setCheckoutError(t('publicEntry.privateBookings.checkout.unavailableError'))
            return
        }
        saveGuestContactDraft({email, firstName, lastName, phone})
        setCheckoutStep('review')
    }

    async function handleCommit() {
        if (!preview || commitInFlightRef.current) return
        commitInFlightRef.current = true
        setCheckoutError(null)
        setIsCommitting(true)
        try {
            const committed = heldCheckout ?? await commitCheckout(preview)
            // The button stays busy until the payment page opens.
            redirectToPayment(await startGuestCheckoutPayment(committed.recoveryToken))
        } catch (error) {
            const message = guestCheckoutErrorMessage(error, t)
            const signInRequired = error instanceof GuestCheckoutError && error.code === 'login_required'
            if (error instanceof GuestCheckoutError && (RESTART_CODES.has(error.code) || signInRequired)) {
                resetCheckoutProgress()
            }
            // Back on the selection, the chosen slot is kept and signing in is offered instead of booking.
            if (signInRequired) setPreviewRequiresSignIn(true)
            setCheckoutError(message)
            commitInFlightRef.current = false
            setIsCommitting(false)
        }
    }

    async function commitCheckout(preview: GuestCheckoutPreview): Promise<GuestCheckoutCommit> {
        const contact = {
            email: customerForm.email.trim(),
            firstName: customerForm.firstName.trim(),
            lastName: customerForm.lastName.trim(),
            phone: customerForm.phone.trim(),
        }
        const consents = {
            fitroTermsAccepted: customerForm.fitroTermsAccepted,
            marketingConsent: customerForm.marketingConsent,
            privacyAccepted: customerForm.termsAccepted,
            termsAccepted: customerForm.termsAccepted,
        }
        const request = JSON.stringify([preview.previewId, contact, consents])
        if (commitAttemptRef.current?.request !== request) {
            commitAttemptRef.current = {idempotencyKey: crypto.randomUUID(), request}
        }
        const committed = await commitGuestCheckout(
            partnerSlug, preview.previewId, contact, consents, commitAttemptRef.current.idempotencyKey,
        ).catch((failure: unknown) => {
            if (isGuestCheckoutCommitRefusal(failure)) commitAttemptRef.current = null
            throw failure
        })
        setHeldCheckout(committed)
        return committed
    }

    function selectOffer(offerId: string) {
        const nextOffer = offers.find((offer) => offer.id === offerId)
        if (!nextOffer) return
        const nextInstructorOptions = buildInstructorOptions(nextOffer, t('publicEntry.privateBookings.anyInstructor'))
        setSelectedOfferId(nextOffer.id)
        setSelectedInstructorId(isAutoAssignedTrainerOffer(nextOffer) ? ANY_INSTRUCTOR_ID : nextInstructorOptions[0]?.id ?? ANY_INSTRUCTOR_ID)
        setSelectedSlotStartAt('')
        resetCheckoutProgress()
    }

    function renderServiceSelect(idPrefix: string) {
        return (
            <PrivateBookingOfferSelector
                ariaLabel={t('publicEntry.privateBookings.steps.service')}
                idPrefix={`${idPrefix}-service`}
                items={offers.map((offer) => ({
                    description: offer.description,
                    details: `${offer.durationMinutes} min · ${formatPrivateBookingPrice(offer, locale)}`,
                    id: offer.id,
                    name: offer.name,
                }))}
                placeholder={t('publicEntry.privateBookings.steps.service')}
                selectedOfferId={hasSelectedOffer ? selectedOffer.id : null}
                onSelect={selectOffer}
                variant="public"
            />
        )
    }

    function renderInstructorSelect(idPrefix: string) {
        return (
            <PublicPrivateBookingInstructorSelector
                ariaLabel={t('publicEntry.privateBookings.steps.instructor')}
                cancelText={tCommon('cancel', 'Anuluj')}
                disabled={!hasSelectedOffer}
                idPrefix={`${idPrefix}-instructor`}
                items={instructorOptions}
                placeholder={hasSelectedOffer
                    ? t('publicEntry.privateBookings.anyInstructor')
                    : t('publicEntry.privateBookings.selectServiceFirst')}
                selectedInstructorId={selectedInstructor?.id ?? null}
                onSelect={(instructorId) => {
                    setSelectedInstructorId(instructorId)
                    resetCheckoutProgress()
                }}
            />
        )
    }

    function renderDayPicker() {
        const renderPreviousWeekButton = () => (
            <button
                type="button"
                aria-label={t('publicEntry.schedule.previousWeek')}
                className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-md border transition-[border-color,background-color,color]',
                    weekOffset === 0
                        ? 'cursor-not-allowed border-[var(--landing-neutral-100)] bg-[var(--landing-neutral-50)] text-[var(--landing-neutral-300)]'
                        : 'border-[var(--landing-neutral-100)] bg-white text-[var(--landing-neutral-600)] hover:border-[var(--landing-primary-200)] hover:text-[var(--landing-primary-600)]',
                )}
                disabled={!hasSelectedOffer || weekOffset === 0}
                onClick={() => selectWeek(Math.max(weekOffset - 1, 0))}
            >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
        )

        const renderNextWeekButton = () => (
            <button
                type="button"
                aria-label={t('publicEntry.schedule.nextWeek')}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[var(--landing-neutral-100)] bg-white text-[var(--landing-neutral-600)] transition-[border-color,background-color,color] hover:border-[var(--landing-primary-200)] hover:text-[var(--landing-primary-600)] disabled:cursor-not-allowed disabled:bg-[var(--landing-neutral-50)] disabled:text-[var(--landing-neutral-300)]"
                disabled={!hasSelectedOffer}
                onClick={() => selectWeek(weekOffset + 1)}
            >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
        )

        return (
            <div role="group" aria-label={t('publicEntry.privateBookings.steps.day')}>
                <div className="space-y-2">
                    <div className="grid grid-cols-[40px_minmax(0,1fr)_40px] items-center gap-2">
                        {renderPreviousWeekButton()}
                        <p className="truncate text-center text-[14px] font-semibold leading-[1.3] text-[var(--landing-neutral-900)] sm:text-[15px]">
                            {weekRangeLabel}
                        </p>
                        {renderNextWeekButton()}
                    </div>
                    <PublicScheduleDayPicker
                        days={visibleBookingDays}
                        selectedDateKey={selectedDay?.key ?? null}
                        todayKey={todayKey}
                        formatFullWeekday={(dateKey) => dateTime.formatLocalDateWeekday(dateKey, {width: 'long', context: 'sentence'})}
                        formatMobileWeekday={(dateKey) => dateTime.formatLocalDateWeekday(dateKey, {width: 'short', context: 'sentence'})}
                        formatDayNumber={dateTime.formatLocalDateDay}
                        formatDayMonth={(dateKey) => dateTime.formatLocalDate(dateKey, {style: 'dayMonth'})}
                        isDayDisabled={(day) => !hasSelectedOffer || (!slotsLoading && !daysWithSlots.has(day.key))}
                        onSelectDay={selectDay}
                    />
                </div>
            </div>
        )
    }

    function renderHoursPicker() {
        if (!hasSelectedOffer) {
            return <BookingSlotsEmptyState title={t('publicEntry.privateBookings.selectServiceFirst')} />
        }

        return (
            <>
                <div aria-live="polite" aria-busy={slotsLoading}>
                    {slotsLoading ? (
                        <div className="grid grid-cols-2 gap-2 opacity-80 sm:grid-cols-4">
                            {Array.from({length: 8}, (_, index) => (
                                <div
                                    key={index}
                                    className="h-10 motion-safe:animate-pulse motion-reduce:animate-none rounded-md border border-[var(--landing-neutral-100)] bg-[var(--landing-neutral-50)]"
                                />
                            ))}
                        </div>
                    ) : null}
                    {!slotsLoading && slotsError ? (
                        <BookingSlotsEmptyState title={t('publicEntry.privateBookings.checkout.noSlotsWeek')} />
                    ) : null}
                    {!slotsLoading && !slotsError && slotsForSelectedDay.length === 0 ? (
                        <BookingSlotsEmptyState
                            title={t(slots.length === 0
                                ? 'publicEntry.privateBookings.checkout.noSlotsWeek'
                                : 'publicEntry.privateBookings.checkout.noSlotsDay')}
                        />
                    ) : null}
                    {!slotsLoading && !slotsError && slotsForSelectedDay.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                            {slotsForSelectedDay.map((slot) => {
                                const selected = slot.startAt === selectedSlotStartAt
                                const hour = dateTime.formatInstantTime(slot.startAt)
                                return (
                                    <button
                                        key={slot.startAt}
                                        type="button"
                                        aria-pressed={selected}
                                        className={cn(
                                            'h-10 rounded-md border text-[14px] font-semibold transition-[border-color,background-color,color]',
                                            selected
                                                ? 'border-[var(--landing-primary-500)] bg-[linear-gradient(16deg,var(--landing-primary-600)_0%,var(--landing-secondary-500)_100%)] text-white'
                                                : 'border-[var(--landing-neutral-100)] bg-white text-[var(--landing-neutral-800)] hover:border-[var(--landing-primary-200)]',
                                        )}
                                        onClick={() => {
                                            setSelectedSlotStartAt(slot.startAt)
                                            resetCheckoutProgress()
                                        }}
                                    >
                                        {hour}
                                    </button>
                                )
                            })}
                        </div>
                    ) : null}
                </div>
            </>
        )
    }

    function renderMobileVisitAction() {
        if (!hasSelectedOffer) {
            return (
                <div className="mt-5 rounded-[12px] border border-[var(--landing-neutral-100)] bg-white/95 p-2.5 shadow-[0_14px_34px_rgba(57,98,198,0.12)] backdrop-blur sm:p-4">
                    <button type="button" className={primaryButtonClassName} disabled>
                        {t('publicEntry.privateBookings.steps.service')}
                    </button>
                </div>
            )
        }

        const canContinue = Boolean(selectedSlot?.startAt && (
            usesAutoAssignedTrainer || (bookingInstructor && bookingInstructor.id !== ANY_INSTRUCTOR_ID)
        ))
        return (
            <div className="mt-5 rounded-[12px] border border-[var(--landing-neutral-100)] bg-white/95 p-2.5 shadow-[0_14px_34px_rgba(57,98,198,0.18)] backdrop-blur sm:p-4">
                <div className="grid gap-1 text-[13px] leading-[1.35] text-[var(--landing-neutral-500)]">
                    <div className="flex items-center justify-between gap-3">
                        <span className="min-w-0 truncate font-semibold text-[var(--landing-neutral-900)]">{selectedOffer.name}</span>
                        <span className="shrink-0 font-bold text-[var(--landing-neutral-900)]">{chargedPrice}</span>
                    </div>
                    <span>
                        {selectedDay && selectedHour ? `${selectedHour} · ${selectedDay.fullLabel}` : t('publicEntry.privateBookings.checkout.noSlots')}
                    </span>
                </div>
                {checkoutError ? renderCheckoutError('mt-3') : null}
                {requiresSignIn ? renderSignInAction() : (
                    <button
                        type="button"
                        className={`${primaryButtonClassName} mt-4 disabled:cursor-not-allowed`}
                        disabled={!canContinue || isPreviewing}
                        onClick={() => void goToCheckoutDetails()}
                    >
                        {isPreviewing ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite]" aria-hidden="true" /> : null}
                        {t('publicEntry.privateBookings.book')}
                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                )}
            </div>
        )
    }

    function renderVisitSummary(showAction = true) {
        const previewInstructorLabel = preview?.summary.instructorName ?? null
        const instructorLabel = usesAutoAssignedTrainer
            ? previewInstructorLabel ?? t('publicEntry.privateBookings.anyInstructor')
            : bookingInstructor?.id === ANY_INSTRUCTOR_ID
                ? t('publicEntry.privateBookings.anyInstructor')
                : bookingInstructor?.label ?? t('publicEntry.privateBookings.anyInstructor')
        const bookingTime = selectedDay && selectedHour ? `${selectedHour} · ${selectedDay.fullLabel}` : ''
        const instructorPeople = instructorFactPeople(instructorLabel)

        return (
            <aside className="flex flex-col rounded-[12px] border border-[var(--landing-neutral-100)] bg-white p-5 shadow-[0_10px_24px_rgba(57,98,198,0.06)] lg:p-6">
                <h3 className="text-[18px] font-semibold leading-[1.25] text-[var(--landing-neutral-900)]">
                    {t('publicEntry.privateBookings.details')}
                </h3>
                {/* The same icon facts as a class page, one per line: a visit's facts do not fit side by side. */}
                <DetailsFacts layout="column" className="mt-5">
                    <DetailsFact label={t('publicEntry.privateBookings.classLabel')} icon={<DetailsFactIcon muted={false}><CalendarCheck className="h-4 w-4"/></DetailsFactIcon>}>
                        {hasSelectedOffer ? selectedOffer.name : '—'}
                    </DetailsFact>
                    <DetailsFact label={t('publicEntry.privateBookings.instructor')} icon={<DetailsPeopleIcon people={instructorPeople} muted={false}/>}>
                        {hasSelectedOffer ? instructorLabel : '—'}
                    </DetailsFact>
                    <DetailsFact label={t('publicEntry.privateBookings.time')} icon={<DetailsFactIcon muted={false}><Clock3 className="h-4 w-4"/></DetailsFactIcon>}>
                        {bookingTime || '—'}
                    </DetailsFact>
                    <DetailsFact label={t('publicEntry.details.location')} icon={<DetailsFactIcon muted={false}><MapPin className="h-4 w-4"/></DetailsFactIcon>}>
                        {hasSelectedOffer ? selectedOfferLocation : '—'}
                    </DetailsFact>
                    <DetailsFact label={t('publicEntry.events.price')} icon={<DetailsFactIcon muted={false}><Ticket className="h-4 w-4"/></DetailsFactIcon>}>
                        <span className="text-base leading-6">{hasSelectedOffer ? chargedPrice : '—'}</span>
                    </DetailsFact>
                    {hasSelectedOffer && selectedOffer.trainerConfirmationRequired ? (
                        <PublicStudioConfirmationFact
                            heading={t('publicEntry.privateBookings.checkout.additionalInformation')}
                            label={t('publicEntry.privateBookings.checkout.waitsForStudio')}
                            explanation={t('publicEntry.privateBookings.checkout.trainerConfirmationExplanation')}
                            helpLabel={t('publicEntry.privateBookings.checkout.confirmationHelpLabel')}
                        />
                    ) : null}
                </DetailsFacts>
                {showAction && checkoutError ? renderCheckoutError('mt-5') : null}
                {showAction && requiresSignIn ? renderSignInAction() : null}
                {showAction && !requiresSignIn ? (
                    <button
                        type="button"
                        className={`${primaryButtonClassName} mt-4`}
                        disabled={!hasSelectedOffer || !selectedSlot || isPreviewing}
                        onClick={() => void goToCheckoutDetails()}
                    >
                        {isPreviewing ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite]" aria-hidden="true" /> : null}
                    {t('publicEntry.privateBookings.book')}
                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                ) : null}
            </aside>
        )
    }

    function renderSignInAction() {
        return (
            <Link href={signInHref} className={`${primaryButtonClassName} mt-4`}>
                {t('publicEntry.guestCheckout.signIn')}
            </Link>
        )
    }

    function renderReviewActions(variant: 'stacked' | 'desktop' = 'stacked') {
        return (
            <div className="space-y-3">
                {checkoutError ? renderCheckoutError() : null}
                {heldCheckout ? (
                    <p className="rounded-md border border-success-200 bg-success-50 p-3 text-[12px] leading-[1.35] text-success-700">
                        {t('publicEntry.guestCheckout.placeHeld', {
                            email: customerForm.email.trim(),
                            time: dateTime.formatInstantTime(heldCheckout.holdExpiresAt),
                        })}
                    </p>
                ) : null}
                <div className={cn(
                    'flex gap-2',
                    variant === 'desktop' ? 'flex-row items-center' : 'flex-col-reverse',
                )}>
                    <button
                        type="button"
                        className={cn(secondaryButtonClassName, variant === 'desktop' && 'w-[128px] shrink-0')}
                        onClick={() => setCheckoutStep('details')}
                        disabled={isCommitting || heldCheckout !== null}
                    >
                        {t('publicEntry.privateBookings.checkout.back')}
                    </button>
                    <button
                        type="button"
                        className={cn(primaryButtonClassName, variant === 'desktop' && 'min-w-0 flex-1')}
                        onClick={handleCommit}
                        disabled={isCommitting}
                    >
                        {isCommitting ? (
                            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite]" aria-hidden="true" />
                        ) : null}
                        {isCommitting
                            ? t('publicEntry.guestCheckout.processing')
                            : t('publicEntry.privateBookings.checkout.payOnline')}
                    </button>
                </div>
            </div>
        )
    }

    function renderCheckoutError(className?: string) {
        return (
            <div role="alert" className={cn('flex gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-[12px] leading-[1.35] text-red-700', className)}>
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <div>
                    <p>{checkoutError}</p>
                </div>
            </div>
        )
    }

    function renderDesktopReview() {
        const previewInstructorLabel = preview?.summary.instructorName ?? null
        const instructorLabel = usesAutoAssignedTrainer
            ? previewInstructorLabel ?? t('publicEntry.privateBookings.anyInstructor')
            : bookingInstructor?.id === ANY_INSTRUCTOR_ID
                ? t('publicEntry.privateBookings.anyInstructor')
                : bookingInstructor?.label ?? t('publicEntry.privateBookings.anyInstructor')
        const bookingTime = selectedDay && selectedHour ? `${selectedHour} · ${selectedDay.fullLabel}` : ''
        const instructorPeople = instructorFactPeople(instructorLabel)

        return (
            <div className="hidden rounded-[12px] border border-[var(--landing-neutral-100)] bg-white px-9 py-8 shadow-[0_14px_34px_rgba(57,98,198,0.06)] xl:grid xl:grid-cols-[minmax(0,1.16fr)_minmax(340px,0.84fr)] xl:gap-12">
                <section className="min-w-0" aria-labelledby="public-private-booking-review-title">
                    <h3 id="public-private-booking-review-title" className="text-[20px] font-semibold leading-[1.2] text-[var(--landing-neutral-900)]">
                        {t('publicEntry.privateBookings.checkout.reservationTitle')}
                    </h3>
                    <div className="mt-7 divide-y divide-[var(--landing-neutral-100)]">
                        <DesktopReviewDetailRow
                            icon={<DetailsFactIcon muted={false}><CalendarCheck className="h-4 w-4"/></DetailsFactIcon>}
                            label={t('publicEntry.privateBookings.classLabel')}
                            value={selectedOffer.name}
                        />
                        <DesktopReviewDetailRow
                            icon={<DetailsPeopleIcon people={instructorPeople} muted={false}/>}
                            label={t('publicEntry.privateBookings.instructor')}
                            value={instructorLabel}
                        />
                        <DesktopReviewDetailRow
                            icon={<DetailsFactIcon muted={false}><Clock3 className="h-4 w-4"/></DetailsFactIcon>}
                            label={t('publicEntry.privateBookings.time')}
                            value={bookingTime}
                        />
                        <DesktopReviewDetailRow
                            icon={<DetailsFactIcon muted={false}><MapPin className="h-4 w-4"/></DetailsFactIcon>}
                            label={t('publicEntry.details.location')}
                            value={selectedOfferLocation}
                        />
                    </div>
                </section>
                <section className="border-l border-[var(--landing-neutral-100)] pl-11" aria-label={t('publicEntry.privateBookings.checkout.paymentTitle')}>
                    <h3 className="text-[20px] font-semibold leading-[1.2] text-[var(--landing-neutral-900)]">
                        {t('publicEntry.privateBookings.checkout.paymentTitle')}
                    </h3>
                    <div className="mt-9 border-b border-[var(--landing-neutral-100)] pb-7 text-center">
                        <p className="text-sm font-medium leading-5 text-gray-500">
                            {t('publicEntry.pricing.table.price')}
                        </p>
                        <p className="mt-2 text-[34px] font-bold leading-[1.15] text-[var(--landing-neutral-900)]">
                            {chargedPrice}
                        </p>
                    </div>
                    <div className="mt-6">
                        {renderReviewActions('desktop')}
                    </div>
                </section>
            </div>
        )
    }

    if (checkoutStep !== 'selection') {
        if (checkoutStep === 'review') {
            return (
                <div key="review" className={FADE_IN_CLASS}>
                    <div className="-mx-2 grid gap-4 xl:hidden lg:mx-0 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
                        <div className="hidden lg:block" />
                        <div className="space-y-4">
                            {renderVisitSummary(false)}
                            {renderReviewActions()}
                        </div>
                    </div>
                    {renderDesktopReview()}
                </div>
            )
        }

        return (
            <div key="details" className={cn('-mx-2 grid gap-8 lg:mx-0 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start', FADE_IN_CLASS)}>
                <section ref={checkoutSectionRef} className="min-w-0 scroll-mt-24" aria-labelledby="public-private-booking-checkout-title">
                    {checkoutStep === 'details' ? (
                        <form className="space-y-5" onSubmit={handleDetailsSubmit} noValidate>
                            <div>
                                <h3 id="public-private-booking-checkout-title" className="text-[20px] font-semibold leading-[1.2] text-[var(--landing-neutral-900)]">
                                    {t('publicEntry.privateBookings.checkout.customerTitle')}
                                </h3>
                                <p className="mt-2 text-[13px] leading-[1.45] text-[var(--landing-neutral-500)]">
                                    {t('publicEntry.privateBookings.checkout.customerCopy')}
                                </p>
                            </div>
                            <GuestSignInHint href={signInHref}/>
                            {checkoutError ? renderCheckoutError() : null}
                            <GuestContactFields
                                idPrefix={fieldErrorIdPrefix}
                                contact={customerForm}
                                onContactChange={updateCustomerForm}
                                consents={{
                                    studioTermsAccepted: customerForm.termsAccepted,
                                    fitroTermsAccepted: customerForm.fitroTermsAccepted,
                                    marketingConsent: customerForm.marketingConsent,
                                }}
                                onConsentChange={(field, checked) => updateCustomerForm(field === 'studioTermsAccepted' ? 'termsAccepted' : field, checked)}
                                errors={fieldErrors}
                                policies={{termsUrl: preview?.termsUrl, privacyUrl: preview?.privacyUrl, fitroTermsUrl: preview?.fitroTermsUrl}}
                            />
                            {/* The binding step leads on a phone, as in the class booking form. */}
                            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                <button
                                    type="button"
                                    className={`${secondaryButtonClassName} sm:w-auto`}
                                    onClick={resetCheckoutProgress}
                                >
                                    {t('publicEntry.privateBookings.checkout.back')}
                                </button>
                                <button
                                    type="submit"
                                    className={`${primaryButtonClassName} sm:w-auto`}
                                >
                                    {t('publicEntry.privateBookings.checkout.continue')}
                                </button>
                            </div>
                        </form>
                    ) : null}

                </section>
                {renderVisitSummary(false)}
            </div>
        )
    }

    return (
        <>
            <div className="-mx-2 lg:hidden">
                <div className="space-y-0">
                    <FlowStep index={1} title={t('publicEntry.privateBookings.steps.service')}>
                        {renderServiceSelect('public-private-booking-mobile')}
                    </FlowStep>
                    {!usesAutoAssignedTrainer ? (
                        <FlowStep index={2} title={t('publicEntry.privateBookings.steps.instructor')}>
                            {renderInstructorSelect('public-private-booking-mobile')}
                        </FlowStep>
                    ) : null}
                    <FlowStep index={usesAutoAssignedTrainer ? 2 : 3} title={t('publicEntry.privateBookings.steps.day')}>
                        {renderDayPicker()}
                    </FlowStep>
                    <FlowStep index={usesAutoAssignedTrainer ? 3 : 4} title={t('publicEntry.privateBookings.steps.hours')}>
                        {renderHoursPicker()}
                    </FlowStep>
                    {renderMobileVisitAction()}
                </div>
            </div>

            <div className="hidden gap-10 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
                <div className="space-y-0">
                    <FlowStep index={1} title={t('publicEntry.privateBookings.steps.service')}>
                        {renderServiceSelect('public-private-booking-desktop')}
                    </FlowStep>

                    {!usesAutoAssignedTrainer ? (
                        <FlowStep index={2} title={t('publicEntry.privateBookings.steps.instructor')}>
                            {renderInstructorSelect('public-private-booking-desktop')}
                        </FlowStep>
                    ) : null}

                    <FlowStep index={usesAutoAssignedTrainer ? 2 : 3} title={t('publicEntry.privateBookings.steps.day')}>
                        {renderDayPicker()}
                    </FlowStep>

                    <FlowStep index={usesAutoAssignedTrainer ? 3 : 4} title={t('publicEntry.privateBookings.steps.hours')}>
                        {renderHoursPicker()}
                    </FlowStep>
                </div>

                {renderVisitSummary()}
            </div>
        </>
    )
}

function FlowStep({children, index, title}: {children: ReactNode; index: number; title: string}) {
    return (
        <div className="border-t border-[var(--landing-neutral-100)] py-5 first:border-t-0 first:pt-0 lg:grid lg:grid-cols-[32px_minmax(0,1fr)] lg:items-start lg:gap-2 lg:py-6 lg:first:pt-0">
            <div className="mb-2 flex items-center gap-3 lg:contents">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--landing-primary-600)] text-[13px] font-semibold text-white">
                    {index}
                </span>
                <h3 className="text-[14px] font-semibold leading-[1.25] text-[var(--landing-neutral-900)] lg:mb-2">
                    {title}
                </h3>
            </div>
            <div className="min-w-0 lg:col-start-2">
                {children}
            </div>
        </div>
    )
}

function DesktopReviewDetailRow({icon, label, value}: {icon: ReactNode; label: string; value: string}) {
    return (
        <div className="grid min-h-[72px] grid-cols-[32px_150px_minmax(0,1fr)] items-center gap-5 py-5 first:pt-0 last:pb-0">
            {icon}
            <span className="text-[14px] font-normal leading-[1.25] text-[var(--landing-neutral-500)]">
                {label}
            </span>
            {value ? (
                <span className="min-w-0 text-[15px] font-semibold leading-[1.35] text-[var(--landing-neutral-900)]">
                    {value}
                </span>
            ) : null}
        </div>
    )
}

function BookingSlotsEmptyState({title}: {title: string}) {
    return (
        <div className="flex min-h-24 items-center justify-center rounded-[12px] border border-[var(--landing-neutral-100)] bg-[var(--landing-neutral-50)] px-4 py-4 text-center sm:min-h-28 sm:py-5 lg:min-h-[144px] lg:py-6">
            <p className="text-[16px] font-semibold leading-[1.35] text-[var(--landing-neutral-900)] sm:text-[17px] lg:text-[18px]">
                {title}
            </p>
        </div>
    )
}

/** A visit the studio still confirms, as one more fact; the help explains what happens next. */
function PublicStudioConfirmationFact({
    explanation,
    heading,
    helpLabel,
    label,
}: {
    explanation: string
    heading: string
    helpLabel: string
    label: string
}) {
    const tooltipId = useId()

    return (
        <DetailsFact label={heading} icon={<DetailsFactIcon muted={false}><Hourglass className="h-4 w-4"/></DetailsFactIcon>}>
            <span className="flex items-start gap-1">
                <span className="min-w-0">{label}</span>
                <span className="group relative -my-1.5 inline-flex shrink-0">
                    <button
                        type="button"
                        aria-label={helpLabel}
                        aria-describedby={tooltipId}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--landing-neutral-400)] outline-hidden transition-colors hover:bg-[var(--landing-neutral-50)] hover:text-[var(--landing-primary-600)] focus-visible:ring-2 focus-visible:ring-[var(--landing-focus-ring)]"
                    >
                        <CircleHelp className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <span
                        id={tooltipId}
                        role="tooltip"
                        className="pointer-events-none absolute bottom-full right-0 z-30 mb-2 hidden w-72 rounded-lg bg-[var(--landing-neutral-900)] px-3 py-2 text-left text-xs font-normal leading-5 text-white shadow-lg group-hover:block group-focus-within:block"
                    >
                        {explanation}
                    </span>
                </span>
            </span>
        </DetailsFact>
    )
}

const ANY_INSTRUCTOR_ID = '__any_instructor__'

const primaryButtonClassName = getButtonClasses({variant: 'primary', className: 'w-full gap-2'})

const secondaryButtonClassName = getButtonClasses({variant: 'secondary', className: 'w-full'})



type CheckoutStep = 'details' | 'review' | 'selection'

type CustomerForm = {
    email: string
    firstName: string
    fitroTermsAccepted: boolean
    marketingConsent: boolean
    lastName: string
    phone: string
    termsAccepted: boolean
}

/** Errors after which the held slot or preview is gone and the guest must pick a slot again. */
const RESTART_CODES = new Set(['checkout_expired', 'full', 'offer_changed', 'preview_expired', 'registration_closed', 'terms_changed'])

type InstructorOption = {
    avatarUrl?: string | null
    id: string
    label: string
}

type SlotInstructorCandidateResponse = {
    instructorId: string
    slots: PublicPrivateBookingSlot[]
}

function isAutoAssignedTrainerOffer(offer: PublicPrivateBookingOffer): boolean {
    return offer.trainerSelectionMode === 'auto_assign'
}

function buildInstructorOptions(offer: PublicPrivateBookingOffer, fallback: string): InstructorOption[] {
    if (offer.instructors.length === 0) {
        return offer.trainerSelectionMode === 'customer_required'
            ? []
            : [{avatarUrl: null, id: ANY_INSTRUCTOR_ID, label: fallback}]
    }
    const instructorChoices = offer.instructors.map((instructor) => ({
        avatarUrl: resolvePublicAssetUrl(instructor.avatarUrl),
        id: instructor.id,
        label: formatInstructorNames([instructor], fallback),
    }))
    // With one trainer there is nothing to choose: that trainer is selected.
    if (offer.trainerSelectionMode === 'customer_required' || instructorChoices.length === 1) {
        return instructorChoices
    }
    return [
        {avatarUrl: null, id: ANY_INSTRUCTOR_ID, label: fallback},
        ...instructorChoices,
    ]
}

function PublicPrivateBookingInstructorSelector({
    ariaLabel,
    cancelText,
    disabled,
    idPrefix,
    items,
    onSelect,
    placeholder,
    selectedInstructorId,
}: {
    ariaLabel: string
    cancelText: string
    disabled: boolean
    idPrefix: string
    items: InstructorOption[]
    onSelect: (instructorId: string) => void
    placeholder: string
    selectedInstructorId?: string | null
}) {
    const [open, setOpen] = useState(false)
    const selectedItem = items.find((item) => item.id === selectedInstructorId) ?? null

    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
            matchTriggerWidth
            modal
            cancelText={cancelText}
            contentRole="dialog"
            contentAriaLabel={ariaLabel}
            className="max-h-[280px] !max-w-none overflow-y-auto p-1 [scrollbar-width:thin]"
            trigger={(
                <button
                    id={`${idPrefix}-trigger`}
                    type="button"
                    aria-label={ariaLabel}
                    disabled={disabled}
                    className="flex min-h-12 w-full items-center justify-between gap-3 rounded-xl border border-[var(--landing-neutral-100)] bg-white px-3 py-1.5 text-left text-[var(--landing-neutral-800)] outline-hidden transition-[border-color,background-color,box-shadow] focus:border-[var(--landing-focus-ring)] focus:ring-2 focus:ring-[var(--landing-primary-100)] disabled:cursor-not-allowed disabled:bg-[var(--landing-neutral-50)] disabled:text-[var(--landing-neutral-400)]"
                >
                    <span className="flex min-w-0 items-center gap-3">
                        {selectedItem?.id === ANY_INSTRUCTOR_ID || !selectedItem ? (
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(237,240,253,0.72)] text-[var(--landing-primary-600)]">
                                <UserRound className="h-4 w-4" aria-hidden="true"/>
                            </span>
                        ) : (
                            <Avatar
                                name={selectedItem.label}
                                src={selectedItem.avatarUrl}
                                size="small"
                                fallbackClassName="bg-[rgba(237,240,253,0.72)] text-[var(--landing-primary-700)]"
                            />
                        )}
                        <span className="truncate text-[14px] font-semibold">{selectedItem?.label ?? placeholder}</span>
                    </span>
                    <ChevronDown className={cn('h-4 w-4 shrink-0 text-[var(--landing-primary-600)] transition-transform duration-normal ease-out motion-reduce:transition-none', open && 'rotate-180')} aria-hidden="true"/>
                </button>
            )}
        >
            <div role="radiogroup" aria-label={ariaLabel}>
                {items.map((item) => {
                    const selected = item.id === selectedInstructorId
                    return (
                        <button
                            key={item.id}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            aria-label={item.label}
                            onClick={() => {
                                onSelect(item.id)
                                setOpen(false)
                            }}
                            className={cn(
                                'flex min-h-14 w-full items-center gap-3 rounded-lg px-3 py-2 text-left outline-hidden transition-colors hover:bg-[var(--landing-neutral-50)] focus-visible:ring-2 focus-visible:ring-[var(--landing-primary-100)]',
                                selected && 'bg-[rgba(237,240,253,0.72)]'
                            )}
                        >
                            {item.id === ANY_INSTRUCTOR_ID ? (
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(237,240,253,0.72)] text-[var(--landing-primary-600)]">
                                    <UserRound className="h-4 w-4" aria-hidden="true"/>
                                </span>
                            ) : (
                                <Avatar
                                    name={item.label}
                                    src={item.avatarUrl}
                                    size="small"
                                    fallbackClassName="bg-[rgba(237,240,253,0.72)] text-[var(--landing-primary-700)]"
                                />
                            )}
                            <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-[var(--landing-neutral-900)]">{item.label}</span>
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                                {selected ? <Check className="h-4 w-4 text-[var(--landing-primary-600)]" aria-hidden="true"/> : null}
                            </span>
                        </button>
                    )
                })}
            </div>
        </Dropdown>
    )
}

function buildBookingDays(dateTime: DateTimeFormatter, weekRange: {startKey: string}) {
    return Array.from({length: 7}, (_, index) => {
        const key = addCalendarDays(weekRange.startKey, index)
        return {
            compactWeekday: dateTime.formatLocalDateWeekday(key, {width: 'narrow', context: 'sentence'}),
            fullLabel: dateTime.formatLocalDate(key, {style: 'long', includeYear: false}),
            isWeekend: isoWeekdayForLocalDate(key) >= 6,
            key,
            label: dateTime.formatLocalDate(key, {style: 'dayMonth'}),
            weekday: dateTime.formatLocalDateWeekday(key, {width: 'short', context: 'sentence'}),
        }
    })
}

function buildBookingWeekRange(todayKey: string, weekOffset = 0) {
    const startKey = addCalendarDays(todayKey, -(isoWeekdayForLocalDate(todayKey) - 1) + weekOffset * 7)
    return {startKey, endKey: addCalendarDays(startKey, 6)}
}

function resolveDefaultBookingDay(todayKey: string, days: ReturnType<typeof buildBookingDays>, weekOffset: number) {
    return weekOffset > 0 ? days[0] : days.find((day) => day.key === todayKey) ?? days[0]
}

function formatBookingWeekRange(dateTime: DateTimeFormatter, weekRange: {endKey: string; startKey: string}) {
    return dateTime.formatLocalDateRange(weekRange.startKey, weekRange.endKey)
}

function formatBookingLocation(profile: PublicPartnerProfile | null, fallback: string): string {
    const city = profile?.address.city
    const street = profile?.address.street
    return [street, city].filter(Boolean).join(', ') || fallback
}

function formatPrivateBookingPrice(offer: Pick<PublicPrivateBookingOffer, 'currency' | 'priceMinor'>, locale: string): string {
    const hasMinorUnits = offer.priceMinor % 100 !== 0
    return new Intl.NumberFormat(locale, {
        currency: offer.currency || 'PLN',
        maximumFractionDigits: hasMinorUnits ? 2 : 0,
        minimumFractionDigits: hasMinorUnits ? 2 : 0,
        style: 'currency',
    }).format(offer.priceMinor / 100).replace(/[\u00a0\u202f]/g, ' ')
}

function dedupeSlotsByStartAt(slots: PublicPrivateBookingSlot[]): PublicPrivateBookingSlot[] {
    const byStartAt = new Map<string, PublicPrivateBookingSlot>()
    slots.forEach((slot) => {
        if (!byStartAt.has(slot.startAt)) {
            byStartAt.set(slot.startAt, slot)
        }
    })
    return Array.from(byStartAt.values()).sort((left, right) => left.startAt.localeCompare(right.startAt))
}

function buildSingleInstructorCandidateMap(
    slots: PublicPrivateBookingSlot[],
    instructorId: string,
): Record<string, string[]> {
    return slots.reduce<Record<string, string[]>>((accumulator, slot) => {
        accumulator[slot.startAt] = [instructorId]
        return accumulator
    }, {})
}

function buildSlotInstructorCandidateMap(
    responses: SlotInstructorCandidateResponse[],
): Record<string, string[]> {
    const candidates: Record<string, string[]> = {}
    responses.forEach((response) => {
        response.slots.forEach((slot) => {
            candidates[slot.startAt] = candidates[slot.startAt] ?? []
            if (!candidates[slot.startAt].includes(response.instructorId)) {
                candidates[slot.startAt].push(response.instructorId)
            }
        })
    })
    return candidates
}

function pickSuggestedInstructorId(candidateIds: string[], seed: string): string | null {
    if (candidateIds.length === 0) return null
    if (candidateIds.length === 1) return candidateIds[0]
    const hash = Array.from(seed).reduce((accumulator, char) => accumulator + char.charCodeAt(0), 0)
    return candidateIds[hash % candidateIds.length]
}

function guestCheckoutErrorMessage(error: unknown, t: (key: string) => string): string {
    // Here `not_found` means the slot stopped being bookable, not that the offer needs an account.
    if (error instanceof GuestCheckoutError && error.code === 'not_found') return t('publicEntry.privateBookings.checkout.slotGoneError')
    return t(`publicEntry.guestCheckout.errors.${error instanceof GuestCheckoutError ? error.code : 'unavailable'}`)
}

function normalizePhoneInput(value: string): string {
    return value.replace(/\D/g, '')
}
